/* eslint-disable */ //TODO:Remove this line after RNT-822
import { IOgqlV1Column } from '@opengov/ogql-types';
import {
  ColumnDefinition,
  ColumnValue,
  ColumnValueSegment,
  IAccountSummary,
  IColumnSource,
  IRowSource,
  MultiHierarchyCalculatedRowGroup,
  MultiHierarchyRowGroup,
  MultiHierarchyRowOverride,
  isCalculationExpression,
  isMultiHierarchyCalculatedRowGroup
} from '@opengov/reporting-types';
import * as api from '../../api/reportingApi';
import { LocaleText } from '../../locale';
import { mapWithConcurrency, MultiGroupingOverflowError, shouldSuppressZerosInRows } from './getRowDataUtils';
import { transformParentRowsToAgGrid, transformChildRowsToAgGrid } from './rowDataTransforms';

/**
 * Row sources reachable from the legacy (non-multi-hierarchy) grid path. Every multi-hierarchy
 * flavour is excluded, including `MultiHierarchyRowOverride` — override rows only ever appear in a
 * `MultiHierarchyAccountSummary`, and routing to this path goes through
 * `isMultiHierarchyAccountSummary`, so one reaching here is a bug the compiler should catch.
 */
export type LegacyRowSource = Exclude<
  IRowSource,
  MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup
>;

export enum DataTableStatus {
  Loading = 'loading',
  Updating = 'updating',
  Current = 'current',
  Error = 'error'
}

export interface QueryContext {
  organizeColumnsByAccountType?: boolean;
  localeText: LocaleText['multidataset'];
  updateTableStatus?: (status: DataTableStatus) => void;
  initialize?: (args: { parentRows: any[] }) => void;
  entitySubdomain?: string;
  entityFiscalYearStartMonth?: number;
  /**
   * Set only for a combined report, from the loaded report's `entityGroupId`. Report-level, unlike
   * the two entity fields above — which chart of accounts a query reads is a property of the saved
   * report, not of who is viewing it.
   */
  entityGroupId?: string;
  /**
   * Upper bound on simultaneous row requests from one fan-out, the same configured limit the
   * prefetch uses. Unset means unbounded, as before the limit existed.
   */
  maxParallelRequests?: number;
}

export { collect } from './getRowDataUtils';

export async function getRowData(
  context: QueryContext,
  report: IAccountSummary,
  reportConfigurationId: string,
  columnValues: ColumnValue[],
  expireIfOlderThan: string,
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[],
  callback?: (values: any[]) => void
) {
  if (!columnValues?.length) {
    return callback?.([]);
  }

  const { rowGroups = [] } = (report ?? {}) as { rowGroups: LegacyRowSource[] };
  const pivotColumnGroupIds = new Set(columnValues.map((col) => col[0]));

  if (report.columnGroups.some((group) => !pivotColumnGroupIds.has(group.id))) {
    return callback?.([]);
  }

  // For any call to get parent rows, we need to get an internal collection of results
  // as they come in. As the rest of the calls arrive, we collect the results and pass
  // them into the success callback. This lets the grid update incrementally.
  const result: Record<number, any> = {};

  const suppressZerosInRows = shouldSuppressZerosInRows(report);
  const lastResolvedRowIndex = { value: 0 };
  return mapWithConcurrency(rowGroups, context.maxParallelRequests, (_, rowIndex) => {
    return api
      .pivotRows(
        {
          report,
          rowIndex,
          parentQuery: true,
          columnValues,
          entitySubdomain: context.entitySubdomain,
          entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
          entityGroupId: context.entityGroupId,
          reportConfigurationId
        },
        { cache: true, expireIfOlderThan }
      )
      .then((response) => {
        if (!response) return;
        lastResolvedRowIndex.value = rowIndex;

        result[rowIndex] = response;
        const responses = [];

        for (let i = 0; i < rowGroups.length; i++) {
          responses.push(result[i]);
        }

        // Aligned by row-group index: a pending group stays `undefined`, so the transform can mark
        // it (pendingValues → loading cells), and a response can never slide onto an earlier
        // group's row while that one is still loading — `collect` compacted the list, which did
        // exactly that whenever responses resolved out of order.
        // Pending means the response has not arrived, not that it has no rows: a response with
        // `data: []` (every account filtered out) is an empty row, so it renders as a dash.
        const rows = responses.map((parentResponse) =>
          parentResponse === undefined ? undefined : ((parentResponse.data?.[0] ?? []) as ColumnValueSegment[])
        );
        const metadata = responses.map(
          (parentResponse) => (parentResponse?.metadata?.columns ?? []) as IOgqlV1Column[]
        );

        const parentRows = transformParentRowsToAgGrid({
          parentRows: rows,
          rowGroups,
          metadataColumnsPerRow: metadata,
          suppressZerosInRows,
          formatting: report.formatting,
          columnGroups,
          columnDefinitions
        });
        callback?.(parentRows);
        return { parentRows, lastResolvedRowIndex };
      });
  }).then((results) => {
    context.updateTableStatus?.(DataTableStatus.Current);
    context.initialize?.({ parentRows: results });
    return results;
  });
}

export interface GetChildDataArgs {
  offset: number;
  limit: number;
  groupingValues: string[];
  rowGroupIndex: number;
  nodeLevel: number;
  parentId?: string;
  parentRowKey?: string;
  reportConfigurationId: string;
  /**
   * Multi-hierarchy only: expand a deepest-level group leaf into its leaf detail rows — account
   * numbers, or segment codes when no Account Number level is configured (lazy reveal).
   */
  expandLeafDetail?: boolean;
  /** Multi-hierarchy only: config index of the breakdown level the expanded row sits on. */
  rowBreakdownLevel?: number;
  /**
   * Multi-hierarchy only: indent steps every row in this fetch inherits from an injected
   * root-level breakdown ancestor. Presentation only — never sent to the backend.
   */
  breakdownIndentLevels?: number;
  /**
   * Multi-hierarchy only: true when the expanded row is a top-level row group row rather than a
   * nested group or an injected root-level breakdown row. Presentation only — never sent to the
   * backend; it decides whether the totals row takes the row group's configured name.
   */
  isRootRowExpand?: boolean;
}

export const sanitizedGroupingValues = (groupingValues: string[]) => {
  const sanitizedGroupingValues = groupingValues.map((value) => value ?? '');
  if (
    sanitizedGroupingValues.filter((val) => {
      return val !== '';
    }).length === 0
  ) {
    return [];
  }
  return sanitizedGroupingValues;
};

export async function getChildData(
  context: QueryContext,
  report: IAccountSummary,
  columnValues: ColumnValue[],
  payload: GetChildDataArgs,
  expireIfOlderThan: string,
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  const { rowGroupIndex, limit, offset, nodeLevel, parentId, groupingValues, reportConfigurationId, parentRowKey } =
    payload;

  const currentRowGroup = report.rowGroups[rowGroupIndex] as LegacyRowSource;
  const suppressZerosInRows = shouldSuppressZerosInRows(report);

  if (
    currentRowGroup &&
    !isCalculationExpression(currentRowGroup) &&
    !isMultiHierarchyCalculatedRowGroup(currentRowGroup) &&
    (currentRowGroup.grouping.length === 0 || nodeLevel > currentRowGroup.grouping.length)
  ) {
    throw new MultiGroupingOverflowError();
  }

  const pivotRows = await api.pivotRows(
    {
      report,
      rowIndex: rowGroupIndex,
      columnValues,
      limit,
      offset,
      groupingValues: sanitizedGroupingValues(groupingValues),
      entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
      entitySubdomain: context.entitySubdomain,
      entityGroupId: context.entityGroupId,
      reportConfigurationId
    },
    {
      cache: true,
      expireIfOlderThan
    }
  );

  return {
    childData: transformChildRowsToAgGrid({
      childRows: pivotRows?.data,
      rowGroup: currentRowGroup,
      rowGroupIndex,
      nodeLevel,
      metadataColumns: pivotRows?.metadata?.columns,
      suppressZerosInRows,
      formatting: report.formatting,
      columnGroups,
      columnDefinitions,
      parentRowGroupId: parentId,
      parentRowKey
    }),
    returnedPivotRowCount: pivotRows?.data?.length
  };
}
